import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList, Shield, Dna, CalendarCheck, Award, Filter, Thermometer,
  Droplets, Lightbulb, Smile, CheckCircle2, Fish, TrendingUp, ArrowRight,
  ArrowLeft, Plus, Minus, ShieldCheck,
} from "lucide-react";
import { Navbar } from "@/components/site/Navbar";

const STEPS = [
  { icon: ClipboardList, title: "Professional Design or Redesign", body: "Your aquarium is installed or redesigned by Seatech Aquariums using proven methods and premium equipment." },
  { icon: Shield, title: "Built to the Seatech Standard", body: "Filtration, temperature control, lighting, RODI water, and life-support systems are carefully configured for long-term success." },
  { icon: Dna, title: "Complete Biological Cycling", body: "Your aquarium is fully cycled and stabilized before the guarantee period begins." },
  { icon: CalendarCheck, title: "Customized Maintenance Program", body: "Your aquarium is professionally maintained according to a service plan tailored to your freshwater, saltwater, or reef environment." },
  { icon: Award, title: "6-Month Livestock Guarantee Begins", body: "Once your aquarium has reached the Seatech Standard and is fully established, your six-month livestock guarantee officially begins." },
];

const STANDARD = [
  { icon: Filter, title: "Professional Filtration", body: "Mechanical, biological, and chemical filtration systems work together to maintain exceptional water quality." },
  { icon: Thermometer, title: "Climate Stability", body: "Temperature management provides a consistent environment for healthy aquatic life." },
  { icon: Droplets, title: "Purified RODI Water", body: "Professionally prepared RODI water helps create clean, stable water chemistry for every aquarium." },
  { icon: Lightbulb, title: "Proper Lighting", body: "Lighting systems are selected based on the specific needs of freshwater plants, marine life, or reef corals to encourage long-term health and growth." },
];

const FLOW = ["Inspection", "Recommendations", "System Improvements", "Cycling", "Maintenance", "Guarantee Activated"];

const MATTERS = [
  { icon: Smile, title: "Greater Peace of Mind", body: "Know your aquarium is professionally maintained and supported by experienced specialists." },
  { icon: CheckCircle2, title: "Proven Systems", body: "Every recommendation follows procedures refined through years of successful freshwater, saltwater, and reef aquarium management." },
  { icon: Fish, title: "Healthy Livestock", body: "Stable environments help support healthier fish, invertebrates, aquatic plants, and coral." },
  { icon: TrendingUp, title: "Long-Term Success", body: "Professional planning and preventative care reduce problems while protecting your investment." },
];

const FAQ = [
  { q: "When does my guarantee begin?", a: "After your aquarium has been professionally installed or redesigned by Seatech, fully cycled, and meets the Seatech Standard while enrolled in a customized maintenance program." },
  { q: "Does the guarantee apply to aquariums installed by another company?", a: "Yes. Once Seatech evaluates, updates, stabilizes, and maintains your aquarium to the Seatech Standard, the guarantee becomes active after the appropriate cycling period." },
  { q: "Does every aquarium receive the same maintenance program?", a: "No. Every maintenance plan is customized based on the size, livestock, equipment, and specific needs of your freshwater, saltwater, or reef aquarium." },
];

const GALLERY = [
  "https://images.pexels.com/photos/37703360/pexels-photo-37703360.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "https://images.unsplash.com/photo-1691387824643-227cc84127cf?q=85&w=1000",
  "https://images.unsplash.com/photo-1544552866-d3ed42536cfd?q=85&w=1000",
  "https://images.pexels.com/photos/15932430/pexels-photo-15932430.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "https://images.unsplash.com/photo-1535591273668-578e31182c4f?q=85&w=1000",
  "https://images.pexels.com/photos/35787439/pexels-photo-35787439.jpeg?auto=compress&cs=tinysrgb&w=1000",
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 44, filter: "blur(6px)" }}
    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Bubbles = () => {
  const bubbles = useMemo(
    () => Array.from({ length: 15 }).map((_, i) => ({
      id: i, left: Math.random() * 100, size: 5 + Math.random() * 18,
      duration: 11 + Math.random() * 12, delay: Math.random() * 10,
    })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {bubbles.map((b) => (
        <motion.span
          key={b.id}
          initial={{ y: "110%", opacity: 0 }}
          animate={{ y: "-15%", opacity: [0, 0.5, 0.5, 0] }}
          transition={{ duration: b.duration, delay: b.delay, repeat: Infinity, ease: "linear" }}
          className="absolute rounded-full border border-[#38D6E8]/30 bg-[#38D6E8]/10 backdrop-blur-[1px]"
          style={{ left: `${b.left}%`, width: b.size, height: b.size }}
        />
      ))}
    </div>
  );
};

const LightRays = () => (
  <div className="pointer-events-none fixed inset-0 overflow-hidden">
    {[16, 48, 80].map((left, i) => (
      <motion.div
        key={i}
        animate={{ opacity: [0.04, 0.14, 0.04] }}
        transition={{ duration: 10 + i * 2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-1/3 h-[160%] w-44 rotate-12 bg-gradient-to-b from-[#dfe9f2]/30 via-[#38D6E8]/10 to-transparent blur-2xl"
        style={{ left: `${left}%` }}
      />
    ))}
  </div>
);

export default function Guarantee() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);

  const goHome = (target) => navigate("/", { state: { scrollTarget: target } });

  return (
    <main data-testid="guarantee-page" className="relative min-h-screen bg-[#071A29] text-[#F7FAFC]">
      <div className="fixed inset-0 z-0">
        <video className="absolute inset-0 h-full w-full object-cover" src="/media/reef-loop.mp4" autoPlay loop muted playsInline data-testid="guarantee-bg-video" />
        <div className="absolute inset-0 bg-[#050d1a]/90" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#04101c]/93 via-[#0a1626]/86 to-[#04101c]/97" />
      </div>

      <LightRays />

      <div className="relative z-10">
        <Navbar />

        {/* HERO */}
        <section className="relative flex min-h-[58vh] items-center overflow-hidden px-6 lg:px-16 pt-52 pb-10">
          <Bubbles />
          <div className="relative mx-auto flex w-full max-w-4xl flex-col items-center text-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#e8c477]/40 bg-[#e8c477]/10 px-4 py-2 font-body text-xs font-medium uppercase tracking-[0.28em] text-[#f0d69a]" data-testid="guarantee-kicker">
                <ShieldCheck className="h-4 w-4" /> The Seatech Standard
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 font-title text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl" data-testid="guarantee-headline">
                Protected by the Seatech<br />6-Month Livestock Guarantee
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-2xl font-body text-base font-light leading-relaxed text-[#c6d6e4] lg:text-lg">
                When your aquarium is professionally designed or redesigned by Seatech and maintained
                through one of our customized service programs, you receive the confidence of our exclusive
                6-month livestock guarantee.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <button onClick={() => goHome("bottom")} data-testid="guarantee-hero-cta" className="group inline-flex items-center gap-3 rounded-full bg-[#38D6E8] px-8 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[#04212a] transition-all duration-300 hover:shadow-[0_0_40px_-8px_#38D6E8]">
                  Schedule a Consultation
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <button onClick={() => navigate("/maintenance")} data-testid="guarantee-hero-secondary" className="inline-flex items-center gap-2 rounded-full border border-[#F7FAFC]/25 px-6 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[#F7FAFC]/90 backdrop-blur-md transition-all duration-300 hover:border-[#F7FAFC]/60">
                  Learn About Maintenance Programs
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        {/* OUR PROMISE */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-4xl">
            <Reveal>
              <div className="rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-10 text-center backdrop-blur-2xl lg:p-14" data-testid="guarantee-promise">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl border border-[#e8c477]/30 bg-[#e8c477]/10 text-[#f0d69a] shadow-[0_0_40px_-8px_#e8c477]">
                  <ShieldCheck className="h-10 w-10" strokeWidth={1.3} />
                </div>
                <h2 className="mt-8 font-title text-3xl font-bold tracking-tight lg:text-4xl">We Stand Behind Every Aquarium We Build</h2>
                <p className="mx-auto mt-6 max-w-2xl font-body text-sm font-light leading-relaxed text-[#c6d6e4] lg:text-base">
                  At Seatech Aquariums, our guarantee reflects years of proven experience, careful planning,
                  and consistent professional maintenance. When your aquarium is built according to the
                  Seatech Standard and cared for through our maintenance program, you can enjoy your
                  investment with greater confidence and peace of mind.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* HOW THE GUARANTEE WORKS */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-5xl">
            <Reveal>
              <span className="font-body text-xs font-medium uppercase tracking-[0.28em] text-[#7fe8ff]">Activation Process</span>
              <h2 className="mt-3 font-title text-3xl font-bold tracking-tight lg:text-4xl">How the Guarantee Works</h2>
            </Reveal>
            <div className="relative mt-14">
              <div className="absolute left-7 top-0 bottom-0 hidden w-px bg-gradient-to-b from-[#e8c477]/50 via-[#F7FAFC]/15 to-transparent md:block" />
              <div className="space-y-6">
                {STEPS.map((s, i) => (
                  <Reveal key={s.title} delay={i * 0.06}>
                    <div className="group relative flex items-start gap-6 rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-6 backdrop-blur-2xl transition-all duration-500 hover:border-[#38D6E8]/45 md:pl-8" data-testid={`guarantee-step-${i}`}>
                      <div className={`relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border transition-all duration-500 ${i === STEPS.length - 1 ? "border-[#e8c477]/40 bg-[#e8c477]/10 text-[#f0d69a] group-hover:shadow-[0_0_30px_-6px_#e8c477]" : "border-[#38D6E8]/30 bg-[#38D6E8]/10 text-[#7fe8ff] group-hover:shadow-[0_0_30px_-6px_#38D6E8]"}`}>
                        <s.icon className="h-6 w-6" strokeWidth={1.5} />
                      </div>
                      <div>
                        <span className="font-body text-xs font-medium uppercase tracking-[0.24em] text-[#7fe8ff]">Step {i + 1}</span>
                        <h3 className="mt-1 font-title text-lg font-bold tracking-tight">{s.title}</h3>
                        <p className="mt-2 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">{s.body}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* THE SEATECH STANDARD */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">The Seatech Standard</h2></Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {STANDARD.map((c, i) => (
                <Reveal key={c.title} delay={i * 0.1}>
                  <div className="group h-full rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-7 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#38D6E8]/45 hover:shadow-[0_20px_60px_-30px_#38D6E8]" data-testid={`guarantee-standard-${i}`}>
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#38D6E8]/25 bg-[#38D6E8]/10 text-[#7fe8ff]">
                      <c.icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-5 font-title text-lg font-bold leading-snug tracking-tight">{c.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">{c.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ALREADY HAVE AN AQUARIUM */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <span className="font-body text-xs font-medium uppercase tracking-[0.28em] text-[#7fe8ff]">Already Have an Aquarium?</span>
              <h2 className="mt-3 font-title text-3xl font-bold tracking-tight lg:text-4xl">Switching to Seatech Is Easy</h2>
              <p className="mt-5 font-body text-sm font-light leading-relaxed text-[#c6d6e4] lg:text-base">
                If your aquarium was installed or maintained by another provider, Seatech will begin with a
                comprehensive consultation and inspection to evaluate your system. Our team will identify
                opportunities for improvement and recommend any upgrades needed to bring your aquarium up to
                the Seatech Standard. Once stabilized, properly cycled, and maintained through your customized
                service program, your six-month livestock guarantee becomes active.
              </p>
            </Reveal>
            <div className="space-y-4">
              {FLOW.map((step, i) => (
                <Reveal key={step} delay={i * 0.08}>
                  <div className={`flex items-center gap-5 rounded-2xl border bg-[#0b1626]/50 px-6 py-4 backdrop-blur-2xl transition-all duration-500 ${i === FLOW.length - 1 ? "border-[#e8c477]/45" : "border-[#F7FAFC]/12 hover:border-[#38D6E8]/45"}`} data-testid={`guarantee-flow-${i}`}>
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border font-title text-sm font-bold ${i === FLOW.length - 1 ? "border-[#e8c477]/40 bg-[#e8c477]/10 text-[#f0d69a]" : "border-[#38D6E8]/30 bg-[#38D6E8]/10 text-[#7fe8ff]"}`}>{i + 1}</span>
                    <span className="font-title text-base font-bold tracking-tight">{step}</span>
                    {i === FLOW.length - 1 && <Award className="ml-auto h-5 w-5 text-[#f0d69a]" />}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* WHY IT MATTERS */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Why It Matters</h2></Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {MATTERS.map((c, i) => (
                <Reveal key={c.title} delay={i * 0.1}>
                  <div className="group h-full rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-7 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#38D6E8]/45 hover:shadow-[0_20px_60px_-30px_#38D6E8]" data-testid={`guarantee-matters-${i}`}>
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#38D6E8]/25 bg-[#38D6E8]/10 text-[#7fe8ff]">
                      <c.icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-5 font-title text-lg font-bold leading-snug tracking-tight">{c.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">{c.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-4xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Frequently Asked Questions</h2></Reveal>
            <div className="mt-10 space-y-4">
              {FAQ.map((e, i) => {
                const open = openFaq === i;
                return (
                  <Reveal key={e.q} delay={i * 0.06}>
                    <div className="overflow-hidden rounded-2xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 backdrop-blur-2xl" data-testid={`guarantee-faq-${i}`}>
                      <button onClick={() => setOpenFaq(open ? -1 : i)} data-testid={`guarantee-faq-toggle-${i}`} className="flex w-full items-center justify-between gap-4 px-7 py-6 text-left transition-colors hover:bg-[#38D6E8]/5">
                        <span className="font-title text-lg font-bold tracking-tight">{e.q}</span>
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#38D6E8]/30 bg-[#38D6E8]/10 text-[#7fe8ff]">{open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}</span>
                      </button>
                      <AnimatePresence initial={false}>
                        {open && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: "easeInOut" }}>
                            <p className="px-7 pb-6 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">{e.a}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="relative px-6 lg:px-16 py-32">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#020a16]/82 to-[#020a16]/95" />
          <div className="relative mx-auto max-w-3xl text-center">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-5xl" data-testid="guarantee-final-heading">Invest With Confidence</h2>
              <p className="mx-auto mt-6 max-w-2xl font-body text-sm font-light leading-relaxed text-[#c6d6e4] lg:text-base">
                Exceptional aquariums begin with exceptional standards. From custom design and installation to
                ongoing maintenance, Seatech Aquariums is committed to creating thriving aquatic environments
                backed by expertise, consistency, and our exclusive six-month livestock guarantee.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <button onClick={() => goHome("bottom")} data-testid="guarantee-final-cta" className="inline-flex items-center gap-3 rounded-full bg-[#38D6E8] px-9 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[#04212a] transition-all duration-300 hover:shadow-[0_0_44px_-8px_#38D6E8]">Book Your Free Consultation</button>
                <button onClick={() => navigate("/maintenance")} data-testid="guarantee-final-secondary" className="inline-flex items-center gap-2 rounded-full border border-[#F7FAFC]/25 px-8 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[#F7FAFC]/90 backdrop-blur-md transition-all duration-300 hover:border-[#F7FAFC]/60">Explore Maintenance Programs</button>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </main>
  );
}
